import { Redirect, useSegments } from "expo-router";

import { ADMIN_ONLY } from "@/config";

/**
 * Keeps the admin host inside `(app)` (TRO-664, TRO-667).
 *
 * `/account-deletion`, `/browse/*`, `/shops/*`, `/shows/*` and `/inventory/*` are siblings of
 * `(app)`, so they render outside the `ClerkProvider` that `(app)/_layout` mounts. If the root
 * navigator lands on one and then redirects to `/sign-in`, `(auth)/sign-in` mounts without its
 * provider, the Clerk hook throws, and React aborts with error #185.
 *
 * The redirect must render beside the navigator, never in place of it. `<Redirect>` asks the
 * router to replace the current route; with the only navigator unmounted, the replace never lands,
 * `useSegments()` keeps reporting the sibling, and the guard redirects again until React hits its
 * nested-update limit (#185).
 *
 * Keeping the navigator mounted renders the sibling route for one frame. That is safe because this
 * guard redirects to `/admin`, inside `(app)`, so `(auth)/sign-in` never mounts outside its
 * provider on this path.
 */
export function AdminHostGuard({ children }: { children: React.ReactNode; }) {
  const segments = useSegments();
  const outsideApp = ADMIN_ONLY && segments.length > 0 && segments[0] !== "(app)";
  return (
    <>
      {outsideApp ? <Redirect href="/admin" /> : null}
      {children}
    </>
  );
}
